import React, { useState } from "react";
import type { VoucherInstanceFilters } from "../../services/voucherInstances.service";
import { VOUCHER_TYPE_OPTIONS } from "../../types/voucherTemplate.types";
import Button from "../common/Button";
import Input from "../common/Input";

interface VoucherInstancesFiltersProps {
  filters: VoucherInstanceFilters;
  onFilterChange: (filters: VoucherInstanceFilters) => void;
}

const VoucherInstancesFilters: React.FC<VoucherInstancesFiltersProps> = ({
  filters,
  onFilterChange,
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleStatusChange = (status: string) => {
    const statusArray = filters.status || [];
    const newStatus = statusArray.includes(status)
      ? statusArray.filter((s: string) => s !== status)
      : [...statusArray, status];
    onFilterChange({ ...filters, status: newStatus });
  };

  const handleVoucherTypeToggle = (type: string) => {
    const types = filters.voucher_type || [];
    const newTypes = types.includes(type)
      ? types.filter((t) => t !== type)
      : [...types, type];
    onFilterChange({ ...filters, voucher_type: newTypes });
  };

  const handleDateChange = (
    field:
      | "created_after"
      | "created_before"
      | "redeemed_after"
      | "redeemed_before",
    value: string,
  ) => {
    onFilterChange({ ...filters, [field]: value });
  };

  const handleClearFilters = () => {
    onFilterChange({});
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-4">
      {/* Status Filters */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Status
        </label>
        <div className="flex flex-wrap gap-2">
          {["active", "redeemed", "expired", "cancelled"].map((status) => {
            const isSelected = (filters.status || []).includes(status);
            return (
              <button
                key={status}
                onClick={() => handleStatusChange(status)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors capitalize ${
                  isSelected
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {status}
              </button>
            );
          })}
        </div>
      </div>

      {/* Toggle Advanced */}
      <button
        onClick={() => setShowAdvanced(!showAdvanced)}
        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
      >
        {showAdvanced ? "Hide" : "Show"} Advanced Filters
      </button>

      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="pt-4 border-t border-gray-200 space-y-4">
          {/* Voucher Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Voucher Type
            </label>
            <div className="flex flex-wrap gap-2">
              {VOUCHER_TYPE_OPTIONS.map((option) => {
                const isSelected = (filters.voucher_type || []).includes(
                  option.value,
                );
                return (
                  <button
                    key={option.value}
                    onClick={() => handleVoucherTypeToggle(option.value)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      isSelected
                        ? "bg-primary-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Date Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              type="date"
              label="Created After"
              value={filters.created_after || ""}
              onChange={(e) =>
                handleDateChange("created_after", e.target.value)
              }
            />
            <Input
              type="date"
              label="Created Before"
              value={filters.created_before || ""}
              onChange={(e) =>
                handleDateChange("created_before", e.target.value)
              }
            />
            <Input
              type="date"
              label="Redeemed After"
              value={filters.redeemed_after || ""}
              onChange={(e) =>
                handleDateChange("redeemed_after", e.target.value)
              }
            />
            <Input
              type="date"
              label="Redeemed Before"
              value={filters.redeemed_before || ""}
              onChange={(e) =>
                handleDateChange("redeemed_before", e.target.value)
              }
            />
          </div>
        </div>
      )}

      {/* Actions */}
      {(filters.status?.length ||
        filters.voucher_type?.length ||
        filters.created_after ||
        filters.created_before) && (
        <div className="flex justify-end pt-2">
          <Button variant="secondary" size="sm" onClick={handleClearFilters}>
            Clear All Filters
          </Button>
        </div>
      )}
    </div>
  );
};

export default VoucherInstancesFilters;
